import { useState, useEffect, useMemo, useRef } from "react";
import { BarChart3, Loader2, AlertTriangle, CheckCircle2, Sparkles, RotateCcw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../contexts/AuthContext";

// Fix HF (2026-10-20): "Simulador de Curva de Personal" (Curva en S) dentro del Análisis Financiero.
// Modela mes a mes cuánto personal habrá en obra, con barras que se arrastran con el mouse (o el dedo)
// o con la fila de personal editable. Horas consumidas = Σ (personal del mes × hs mensuales por hombre);
// saldo = tope de H.H. del presupuesto − horas consumidas. Con saldo ≥ 0 el panel se ve en verde;
// con saldo < 0 se ve en rojo y no deja guardar.
const MAX_MESES = 120;
const MAX_PERSONAL = 10000;
const ALTO = 250, M_IZQ = 40, M_DER = 12, M_ARR = 14, M_ABA = 26;

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const entero = (v) => Math.max(0, Math.min(MAX_PERSONAL, Math.round(num(v))));
const fmtN = (n) => Math.round(Number(n) || 0).toLocaleString("es-PY");
const suave = (t) => { const x = Math.max(0, Math.min(1, t)); return x * x * (3 - 2 * x); };

// Distribución en S: sube durante el primer tramo, se sostiene en el máximo y baja al final.
// Nunca supera "hombres" en un mes, así que siempre entra en el tope.
function curvaEnS(n, hombres) {
  return Array.from({ length: n }, (_, i) => {
    const t = (i + 0.5) / n;
    const p = Math.min(suave(t / 0.4), suave((1 - t) / 0.3));
    return Math.max(1, Math.round(hombres * p));
  });
}

export function SimuladorCurvaPersonal({ budgetId, budget, readOnly, hhLive }) {
  const mesesBase = hhLive ? hhLive.meses : num(budget?.hh_meses_duracion);
  const hombres = Math.max(1, Math.round(hhLive ? hhLive.hombres : num(budget?.hombres_obra) || 1));
  const hsMens = hhLive ? hhLive.hsMensXHombre : (num(budget?.hh_hs_lv ?? 40) + num(budget?.hh_hs_sab ?? 5)) * 4;
  const n = Math.max(0, Math.min(MAX_MESES, Math.ceil(mesesBase)));
  const tope = mesesBase * hombres * hsMens;

  const guardadaKey = JSON.stringify(budget?.hh_curva_personal || []);
  const [curva, setCurva] = useState(() => JSON.parse(guardadaKey));
  const [baseline, setBaseline] = useState(() => { const g = JSON.parse(guardadaKey); return g.length ? g : null; });
  const [saving, setSaving] = useState(false);
  const [arrastrando, setArrastrando] = useState(null);
  const svgRef = useRef(null);
  const ejeRef = useRef(0);

  useEffect(() => {
    const g = JSON.parse(guardadaKey);
    setCurva(g);
    setBaseline(g.length ? g : null);
  }, [guardadaKey, budgetId]);

  // Personal de cada mes: lo cargado/guardado; los meses sin dato valen "hombres en obra".
  const valores = useMemo(
    () => Array.from({ length: n }, (_, i) => (curva[i] === undefined ? hombres : entero(curva[i]))),
    [curva, n, hombres]
  );
  const horas = valores.map((v) => v * hsMens);
  const consumidas = horas.reduce((s, h) => s + h, 0);
  const saldo = tope - consumidas;
  const ok = saldo >= 0;
  const usoPct = tope > 0 ? Math.min(100, (consumidas / tope) * 100) : 0;
  const exceso = Math.max(0, -saldo);
  const excesoPersonasMes = hsMens > 0 ? exceso / hsMens : 0;
  const alineada = baseline ? Array.from({ length: n }, (_, i) => (baseline[i] === undefined ? hombres : entero(baseline[i]))) : null;
  const sinCambios = !!alineada && JSON.stringify(alineada) === JSON.stringify(valores);

  const setMes = (i, v) => {
    setCurva((prev) => {
      const sig = Array.from({ length: Math.max(prev.length, n) }, (_, k) => (prev[k] === undefined ? hombres : entero(prev[k])));
      sig[i] = entero(v);
      return sig;
    });
  };

  // Eje vertical: no depende de lo que se arrastra (para que la barra no "huya" del mouse).
  const maxValor = Math.max(0, ...valores);
  const eje = Math.max(4, Math.ceil(hombres * 1.5), maxValor);
  const ejeAct = arrastrando === null ? eje : ejeRef.current;
  const paso = ejeAct <= 12 ? 2 : ejeAct <= 30 ? 5 : ejeAct <= 100 ? 10 : 50;
  const ticks = [];
  for (let t = 0; t <= ejeAct; t += paso) ticks.push(t);

  const anchoPaso = 56;
  const W = M_IZQ + M_DER + Math.max(n, 1) * anchoPaso;
  const altoPlot = ALTO - M_ARR - M_ABA;
  const yDe = (v) => M_ARR + altoPlot * (1 - Math.min(v, ejeAct) / ejeAct);
  const anchoBarra = anchoPaso * 0.56;

  const valorEnY = (clientY) => {
    const svg = svgRef.current;
    if (!svg) return 0;
    const ctm = svg.getScreenCTM();
    if (!ctm) return 0;
    const pt = svg.createSVGPoint();
    pt.x = 0; pt.y = clientY;
    const y = pt.matrixTransform(ctm.inverse()).y;
    const v = Math.round(ejeRef.current * (1 - (y - M_ARR) / altoPlot));
    return Math.max(0, Math.min(ejeRef.current, v));
  };

  const iniciarArrastre = (e, i) => {
    if (readOnly) return;
    e.preventDefault();
    ejeRef.current = eje;
    try { svgRef.current.setPointerCapture(e.pointerId); } catch { /* sin captura */ }
    setArrastrando(i);
    setMes(i, valorEnY(e.clientY));
  };
  const moverArrastre = (e) => {
    if (arrastrando === null) return;
    setMes(arrastrando, valorEnY(e.clientY));
  };
  const terminarArrastre = (e) => {
    if (arrastrando === null) return;
    try { svgRef.current.releasePointerCapture(e.pointerId); } catch { /* ya liberado */ }
    setArrastrando(null);
  };

  const guardar = async () => {
    if (!budgetId) { toast.error("Guardá el presupuesto primero"); return; }
    if (!ok) { toast.error("La simulación supera el tope de horas-hombre: no se puede guardar"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/budgets/${budgetId}`, { hh_curva_personal: valores }, { withCredentials: true });
      setCurva(valores);
      setBaseline(valores);
      toast.success("Simulación de personal guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar la simulación");
    } finally { setSaving(false); }
  };

  const celdas = (fn) => valores.map((v, i) => fn(v, i));

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="simulador-curva-personal">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center"><BarChart3 size={13} className="text-blue-600" /></div>
          <div>
            <h3 className="text-sm font-bold text-gray-800">Simulador de Curva de Personal</h3>
            <p className="text-[11px] text-gray-400">Distribución estimada de la mano de obra mes a mes, dentro del tope de horas-hombre del presupuesto</p>
          </div>
        </div>
        {!readOnly && n > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setCurva(curvaEnS(n, hombres))}
              className="flex items-center gap-1 text-xs text-blue-700 border border-blue-200 hover:bg-blue-50 px-2.5 py-1 rounded-lg transition-colors" data-testid="sim-curva-s-btn">
              <Sparkles size={12} /> Curva en S sugerida
            </button>
            <button onClick={() => setCurva(Array.from({ length: n }, () => hombres))}
              className="flex items-center gap-1 text-xs text-gray-600 border border-gray-200 hover:bg-gray-50 px-2.5 py-1 rounded-lg transition-colors" data-testid="sim-parejo-btn">
              <RotateCcw size={12} /> Distribuir parejo
            </button>
          </div>
        )}
      </div>

      {/* Parámetros base (solo lectura): vienen de la Estimación de Horas Hombre */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 p-3 bg-amber-50 rounded-xl border border-amber-100" data-testid="sim-parametros">
        <div>
          <p className="text-[10px] font-bold text-amber-700 mb-1">Duración obra (meses)</p>
          <p className="text-sm font-bold text-gray-800" data-testid="sim-meses">{mesesBase || "—"}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold text-amber-700 mb-1">Hombres en obra (promedio)</p>
          <p className="text-sm font-bold text-gray-800" data-testid="sim-hombres">{hombres}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold text-amber-700 mb-1">Hs. mensuales × hombre</p>
          <p className="text-sm font-bold text-gray-800" data-testid="sim-hs-mens">{fmtN(hsMens)} h</p>
        </div>
        <div>
          <p className="text-[10px] font-bold text-amber-700 mb-1">Total Hs. Hombre (Presupuesto Tope)</p>
          <p className="text-sm font-bold text-gray-800" data-testid="sim-tope">{fmtN(tope)} hs</p>
        </div>
      </div>

      {n === 0 ? (
        <div className="text-center text-xs text-gray-400 border border-dashed border-gray-200 rounded-xl py-8" data-testid="sim-vacio">
          Cargá la duración de la obra (meses) en la Estimación de Horas Hombre para armar la curva de personal.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-4 items-start">
          <div className="min-w-0">
            <div className="overflow-x-auto">
              <svg ref={svgRef} viewBox={`0 0 ${W} ${ALTO}`} style={{ width: "100%", minWidth: Math.max(460, n * 30), height: "auto", touchAction: "none" }}
                onPointerMove={moverArrastre} onPointerUp={terminarArrastre} onPointerCancel={terminarArrastre}
                role="img" aria-label="Personal estimado por mes" data-testid="sim-grafico">
                {ticks.map((t) => (
                  <g key={t}>
                    <line x1={M_IZQ} x2={W - M_DER} y1={yDe(t)} y2={yDe(t)} stroke="#E5E7EB" strokeWidth="1" />
                    <text x={M_IZQ - 6} y={yDe(t) + 3} textAnchor="end" fontSize="10" fill="#6B7280">{t}</text>
                  </g>
                ))}
                {/* Hombres en obra (promedio) como referencia */}
                {hombres <= ejeAct && (
                  <line x1={M_IZQ} x2={W - M_DER} y1={yDe(hombres)} y2={yDe(hombres)} stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="5 4" data-testid="sim-linea-promedio" />
                )}
                {celdas((v, i) => {
                  const x0 = M_IZQ + i * anchoPaso;
                  const xb = x0 + (anchoPaso - anchoBarra) / 2;
                  const y = yDe(v);
                  const activo = arrastrando === i;
                  return (
                    <g key={i} data-testid={`sim-col-${i + 1}`}>
                      <rect x={xb} y={y} width={anchoBarra} height={Math.max(0, M_ARR + altoPlot - y)} rx="3"
                        fill={activo ? "#1D4ED8" : "#2E6DA4"} opacity={ok ? 1 : 0.85} data-testid={`sim-barra-${i + 1}`} />
                      <text x={xb + anchoBarra / 2} y={y - 9} textAnchor="middle" fontSize="10" fontWeight="700" fill="#1F2937">{v}</text>
                      <text x={x0 + anchoPaso / 2} y={ALTO - 9} textAnchor="middle" fontSize="10" fill="#6B7280">Mes {i + 1}</text>
                      {!readOnly && (
                        <>
                          <rect x={x0} y={0} width={anchoPaso} height={M_ARR + altoPlot + 6} fill="transparent" style={{ cursor: "ns-resize" }}
                            onPointerDown={(e) => iniciarArrastre(e, i)} data-testid={`sim-zona-${i + 1}`} />
                          <rect x={xb + anchoBarra / 2 - 6} y={y - 6} width="12" height="12" rx="2" fill="#fff" stroke="#1D4ED8" strokeWidth="2.5"
                            style={{ cursor: "ns-resize", pointerEvents: "none" }} data-testid={`sim-asa-${i + 1}`} />
                        </>
                      )}
                    </g>
                  );
                })}
                <line x1={M_IZQ} x2={W - M_DER} y1={M_ARR + altoPlot} y2={M_ARR + altoPlot} stroke="#9CA3AF" strokeWidth="1" />
              </svg>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              {!readOnly ? "Arrastrá las barras hacia arriba o abajo, o escribí el personal de cada mes. " : ""}
              La línea naranja marca los hombres en obra (promedio): {hombres}.
            </p>

            <div className="overflow-x-auto mt-3">
              <div className="flex items-stretch gap-1.5 mb-1.5" style={{ minWidth: n * 58 + 96 }}>
                <div className="w-24 shrink-0 text-[10px] font-bold text-gray-600 flex items-center">Personal {readOnly ? "" : "(editable)"}</div>
                {celdas((v, i) => (
                  <div key={i} className="flex-1 min-w-[52px]">
                    <p className="text-[9px] text-center text-gray-400 pb-1">M{i + 1}</p>
                    {readOnly ? (
                      <p className="text-center text-sm font-bold text-gray-800 bg-gray-50 rounded-lg py-1.5" data-testid={`sim-personal-${i + 1}`}>{v}</p>
                    ) : (
                      <input type="number" min="0" step="1" value={v} onChange={(e) => setMes(i, e.target.value)}
                        className="w-full border border-blue-200 rounded-lg px-1 py-1 text-sm text-center font-semibold focus:ring-1 focus:ring-blue-300" data-testid={`sim-personal-${i + 1}`} />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-stretch gap-1.5" style={{ minWidth: n * 58 + 96 }}>
                <div className="w-24 shrink-0 text-[10px] font-bold text-gray-600 flex items-center leading-tight">Horas <span className="font-normal text-gray-400 ml-1">(automático)</span></div>
                {horas.map((h, i) => (
                  <div key={i} className="flex-1 min-w-[52px] text-center text-[11px] font-semibold text-gray-700 bg-gray-100 rounded-lg py-2" data-testid={`sim-horas-${i + 1}`}>{fmtN(h)}</div>
                ))}
              </div>
            </div>
          </div>

          {/* Panel de control: semáforo */}
          <div className={`rounded-xl border p-4 ${ok ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`}
            data-testid="sim-semaforo" data-estado={ok ? "verde" : "rojo"}>
            <div className="flex items-center gap-2 mb-3">
              {ok ? <CheckCircle2 size={18} className="text-emerald-600" /> : <AlertTriangle size={18} className="text-red-600" />}
              <p className={`text-sm font-bold ${ok ? "text-emerald-800" : "text-red-800"}`} data-testid="sim-estado-texto">
                {ok ? "Distribución válida" : "Sobrecosto de mano de obra"}
              </p>
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-gray-600">Horas consumidas</span><span className="font-bold text-gray-800 tabular-nums" data-testid="sim-consumidas">{fmtN(consumidas)} hs</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Presupuesto tope</span><span className="font-bold text-gray-800 tabular-nums">{fmtN(tope)} hs</span></div>
              <div className="flex justify-between border-t pt-1.5 mt-1.5 border-black/10">
                <span className="text-gray-700 font-semibold">Saldo</span>
                <span className={`font-bold tabular-nums ${ok ? "text-emerald-700" : "text-red-700"}`} data-testid="sim-saldo">{saldo < 0 ? "-" : ""}{fmtN(Math.abs(saldo))} hs</span>
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-white/70 mt-3 overflow-hidden">
              <div className={`h-full rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`} style={{ width: `${tope > 0 ? usoPct : (consumidas > 0 ? 100 : 0)}%` }} />
            </div>
            <p className="text-[10px] text-gray-500 mt-1">{tope > 0 ? `${((consumidas / tope) * 100).toFixed(1)}% del tope` : "Sin tope cargado"}</p>
            {!ok && (
              <p className="text-[11px] text-red-700 font-semibold mt-3" data-testid="sim-alerta">
                La simulación supera el tope en {fmtN(exceso)} hs{excesoPersonasMes > 0 ? ` (≈ ${excesoPersonasMes.toFixed(1)} personas-mes)` : ""}. Bajá el personal de algún mes para poder guardarla.
              </p>
            )}
            {!readOnly && (
              <div className="mt-3">
                <button onClick={guardar} disabled={saving || !budgetId || !ok || sinCambios}
                  title={!ok ? "No se puede guardar: supera el tope de horas-hombre" : undefined}
                  className="w-full flex items-center justify-center gap-1 text-xs text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-testid="sim-guardar-btn">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                  Guardar simulación
                </button>
                {ok && sinCambios && <p className="text-[10px] text-emerald-700 mt-1 text-center" data-testid="sim-guardada">Simulación guardada</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
